import { Icon, Tooltip, token } from "@may-i/mash-design-system";
import { type FabricObject, type ModifiedEvent, type TPointerEvent, type TPointerEventInfo, Group, Polygon, Polyline, Rect } from "fabric";
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";
import { DeleteConfirmDialog } from "~/pages/resource-management/DeleteConfirmDialog";
import { CameraScalePopover, ResizeCameraIcon } from "~/widgets/floor-plan/CameraScalePopover";
import type { CountingLine, IntervalZone } from "~/entities/analysis-config/types";
import {
  createFloorPlanMemoDiagramObject,
  createFloorPlanMemoTextObject,
  DEFAULT_DIAGRAM_MEMO_STYLE as DEFAULT_DIAGRAM_STYLE_FPV,
  DEFAULT_TEXT_MEMO_STYLE as DEFAULT_TEXT_STYLE_FPV,
  extractDiagramCoordinates,
  extractTextPosition,
  isFloorPlanMemoObject,
  type TaggedDiagram,
  type TaggedText,
} from "~/pages/camera-mapping/fabric-objects/FloorPlanMemo";
import { resizeCameraIconScale } from "~/_legacy/utils/shape";
import { CameraObject } from "~/pages/camera-mapping/fabric-objects/CameraObject";
import { pickCameraAt } from "~/pages/camera-mapping/fabric-objects/camera-hit";
import { useFloorPlanCanvas } from "~/pages/camera-mapping/useFloorPlanCanvas";
import {
  type Coord,
  type EventSource,
  type FloorPlanDrawingTarget,
  type LineShapeGroup,
  type ShapeGroup,
  type TaggedFabricObject,
  type ZoneShapeGroup,
  BORDER_COLOR,
  CORNER_COLOR,
  CORNER_STROKE_COLOR,
  LINE_NAME_LABEL_STYLE,
  LINE_STROKE,
  LINE_STROKE_WIDTH,
  ZONE_FILL,
  ZONE_LABEL_STYLE,
  ZONE_STROKE,
  getShapeGroup,
  setShapeGroup,
} from "./floor-plan-viewer/constants";
import {
  clampToImageBounds,
  createLabelGroup,
  getBgImage,
  getDynamicLineLabelStyle,
  getImageNaturalSize,
  getNormalizedPoint,
  pixelToPercentStatic,
} from "./floor-plan-viewer/canvas-helpers";
import {
  getLineGroupEndpoints,
  rebuildLineGroup,
  rebuildZonePolygon,
} from "./floor-plan-viewer/shape-helpers";
import {
  enterLineCoordinateMode,
  finishLineCoordinateEdit,
  enterZoneCoordinateMode,
  finishZoneCoordinateEdit,
} from "./floor-plan-viewer/coordinate-editing";
import {
  type DrawingContext,
  type DrawingState,
  handleDrawingClick,
  handleDrawingMove,
  handleDrawingDblClick,
  clearDrawingTemp,
  handleRectDrawingMouseDown,
  handleRectDrawingMouseMove,
  handleRectDrawingMouseUp,
} from "./floor-plan-viewer/drawing-handlers";

export type { FloorPlanDrawingTarget };

interface FloorPlanViewerProps {
  /** 6차 an: cross-canvas 자동 종료용 — 현재 편집 중인 캔버스의 finishEdit 콜백 ref. 부모(AnalysisDraftPage) 가 보유 */
  coordinateEditingFinishRef: MutableRefObject<(() => void) | null>;
  imageUrl: string | null;
  countingLines: CountingLine[];
  intervalZones: IntervalZone[];
  /** PO QA 8차 bf: 같은 도면의 다른 카메라 이벤트소스 — 참조용으로 흐리게·비인터랙티브 렌더 */
  referenceCountingLines?: CountingLine[];
  referenceIntervalZones?: IntervalZone[];
  showLineZone: boolean;
  drawingTarget?: FloorPlanDrawingTarget | null;
  onFloorPlanDrawingComplete?: (type: "line" | "zone", id: number, coordinates: Coord[]) => void;
  /** 4-3-2: 신규 라인/존 그리기 요청 (id 없음, eventType 만). drawingTarget 과 별개. */
  drawingRequest?: { type: "line" | "zone"; eventType: string } | null;
  /** 4-3-2: drawingRequest 완료 시 호출 — AnalysisDraftPage 가 새 라인/존을 생성하면서 floorPlanConfig 에 좌표 저장 */
  onFloorPlanDrawingRequestComplete?: (type: "line" | "zone", eventType: string, coordinates: Coord[]) => void;
  /** 4-3-2: 양쪽 캔버스 중 ownership */
  drawingActiveCanvas?: "snapshot" | "floorPlan" | null;
  /** 4-3-2: 첫 클릭 시 ownership claim */
  onDrawingClaim?: (canvas: "snapshot" | "floorPlan") => void;
  onDrawingCancel?: () => void;
  selectedEventSource?: EventSource | null;
  onSelectionChange?: (sel: EventSource | null) => void;
  onFloorPlanLineCoordinatesChange?: (lineId: number, coordinates: Coord[]) => void;
  onFloorPlanZoneCoordinatesChange?: (zoneId: number, coordinates: Coord[]) => void;
  onRequestFloorPlanDelete?: (type: "line" | "zone", id: number) => void;
  floorPlanMemoDiagrams?: import("~/_legacy/types/analysis").FloorPlanMemoDiagram[];
  floorPlanMemoTexts?: import("~/_legacy/types/analysis").FloorPlanMemoText[];
  onMemoDiagramModified?: (
    memoId: number,
    floorPlanId: number,
    coordinates: { x: number; y: number }[],
    style: import("~/_legacy/types/analysis").FloorPlanMemoDiagram["style"],
  ) => void;
  onMemoTextModified?: (
    memoId: number,
    floorPlanId: number,
    content: string,
    position: { x: number; y: number },
    style: import("~/_legacy/types/analysis").FloorPlanMemoText["style"],
  ) => void;
  onMemoDiagramDelete?: (memoId: number, floorPlanId: number) => void;
  onMemoTextDelete?: (memoId: number, floorPlanId: number) => void;
  onMemoTextCreate?: (floorPlanId: number, position: { x: number; y: number }) => number;
  onMemoDiagramCreate?: (floorPlanId: number, coordinates: { x: number; y: number }[]) => number;
  currentFloorPlanId?: number | null;
  /** (F17) 도면 뷰 상단 오버레이 — 표시할 도면 단일 셀렉트 */
  floorPlans?: import("~/entities/floor-plan/types").FloorPlan[];
  viewerFloorPlanId?: number | null;
  onViewerFloorPlanChange?: (id: number) => void;
  /** 도면에 매핑된 카메라들 — 마커로 표시 (read-only) */
  cameras?: import("~/entities/camera/types").Camera[];
  /** 6차 at: 선택된 카메라 ID — 해당 마커 isFocused 로 렌더해 카메라 매핑 페이지와 동일 select UI */
  selectedCameraId?: number | null;
  /** 7차 az: 도면 카메라 마커 클릭 시 해당 카메라 선택 */
  onCameraMarkerClick?: (camera: import("~/entities/camera/types").Camera) => void;
  /** 도면에서 다른 카메라(참조)의 라인/구역 클릭 → 소속 스냅샷으로 이동 + 포커싱 (v1 복원) */
  onReferenceShapeClick?: (target: { cameraId: number; type: "line" | "zone"; id: number }) => void;
  /** 카메라 분석 번호 매핑 (analysisNumber 가 null 인 임시/N카메라용) */
  cameraDisplayNames?: Map<number, string>;
  /** 도면의 iconSize — 카메라 마커 시각 크기 결정 */
  cameraIconSize?: number;
  /** 도면의 labelSize — 번호 라벨 크기 배수. 도면 축소에 따른 자동 축소 위에 곱해진다 */
  cameraLabelSize?: number;
  /** 크기 조절 팝오버(아이콘/라벨) 노출 + 저장. 없으면 버튼 자체를 띄우지 않음 */
  onScaleCommit?: (params: { iconSize?: number; labelSize?: number }) => void;
  /** PO QA 2차 o: TopOverlay 가 캔버스 상단을 덮는 경우 가용 영역 계산용 padding (px) */
  topPadding?: number;
  /** PO QA 2차 o 후속4: DraftActionBar 가 캔버스 하단을 덮는 경우 가용 영역 계산용 padding (px) */
  bottomPadding?: number;
  /** PO QA 2차 o 후속: 둘 다 모드 아래쪽 캔버스는 "top" anchor 로 위 캔버스와 갭 제거. 기본 "center". */
  verticalAlign?: "center" | "top" | "bottom";
  /** QA-Final B6: 둘 다 모드에서 overlay 높이를 보상해 양쪽 이미지 가용 영역을 동일하게 맞추는 추가 flex-basis (px) */
  extraBasis?: number;
}

function buildDrawingCursor(strokeColor: string): string {
  const r = 4;
  const sw = 1.5;
  const size = (r + sw) * 2;
  const center = size / 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${center}" cy="${center}" r="${r}" fill="white" stroke="${strokeColor}" stroke-width="${sw}"/></svg>`;
  return `url(data:image/svg+xml;base64,${btoa(svg)}) ${center} ${center}, crosshair`;
}

const LINE_DRAWING_CURSOR = buildDrawingCursor(LINE_STROKE);
const ZONE_DRAWING_CURSOR = buildDrawingCursor(ZONE_STROKE);

// F19: Rectangle zone drawing cursor — ic_area_zone icon (rounded square with corner dots)
function buildRectDrawingCursor(fillColor: string): string {
  const size = 22;
  const center = size / 2;
  // ic_area_zone path scaled from 24→22 via viewBox
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
    <path fill-rule="evenodd" clip-rule="evenodd" fill="${fillColor}" d="M2 4.5a2.5 2.5 0 0 0 1.5 2.292v10.416A2.5 2.5 0 1 0 6.792 20.5h10.416a2.5 2.5 0 1 0 3.292-3.292V6.792A2.5 2.5 0 1 0 17.208 3.5H6.792A2.5 2.5 0 0 0 2 4.5Zm16.5 12.708V6.792A2.51 2.51 0 0 1 17.208 5.5H6.792A2.51 2.51 0 0 1 5.5 6.792v10.416A2.51 2.51 0 0 1 6.792 18.5h10.416a2.51 2.51 0 0 1 1.292-1.292Z"/>
  </svg>`;
  return `url(data:image/svg+xml;base64,${btoa(svg)}) ${center} ${center}, crosshair`;
}
const ZONE_RECT_DRAWING_CURSOR = buildRectDrawingCursor(ZONE_STROKE);

export function FloorPlanViewer({
  coordinateEditingFinishRef,
  imageUrl,
  countingLines,
  intervalZones,
  referenceCountingLines = [],
  referenceIntervalZones = [],
  showLineZone,
  drawingTarget,
  onFloorPlanDrawingComplete,
  drawingRequest,
  onFloorPlanDrawingRequestComplete,
  drawingActiveCanvas = null,
  onDrawingClaim,
  onDrawingCancel,
  selectedEventSource,
  onSelectionChange,
  onFloorPlanLineCoordinatesChange,
  onFloorPlanZoneCoordinatesChange,
  onRequestFloorPlanDelete,
  floorPlanMemoDiagrams,
  floorPlanMemoTexts,
  onMemoDiagramModified,
  onMemoTextModified,
  onMemoDiagramDelete,
  onMemoTextDelete,
  onMemoTextCreate,
  onMemoDiagramCreate,
  currentFloorPlanId,
  floorPlans = [],
  viewerFloorPlanId,
  onViewerFloorPlanChange,
  cameras = [],
  selectedCameraId,
  onCameraMarkerClick,
  onReferenceShapeClick,
  cameraDisplayNames,
  cameraIconSize = 60,
  cameraLabelSize = 1,
  onScaleCommit,
  topPadding = 0,
  bottomPadding = 0,
  verticalAlign = "center",
  extraBasis = 0,
}: FloorPlanViewerProps) {
  const { t } = useTranslation("analysis-draft");
  // 아이콘/라벨 크기 — 드래그 중엔 로컬 상태로 즉시 반영하고, 손 뗄 때 onScaleCommit 으로 저장.
  // 저장 후 refetch 되면 prop 이 같은 값으로 돌아오므로 아래 sync effect 는 no-op.
  const [showScalePopover, setShowScalePopover] = useState(false);
  const [iconSizeLocal, setIconSizeLocal] = useState(cameraIconSize);
  const [labelSizeLocal, setLabelSizeLocal] = useState(cameraLabelSize);
  useEffect(() => setIconSizeLocal(cameraIconSize), [cameraIconSize]);
  useEffect(() => setLabelSizeLocal(cameraLabelSize), [cameraLabelSize]);
  // (F17) 표시할 도면 단일 셀렉트 (캔버스 좌상단 오버레이)
  const [viewSelectOpen, setViewSelectOpen] = useState(false);
  const viewerFloorPlanName = floorPlans.find((fp) => fp.id === viewerFloorPlanId)?.name ?? "";
  const { t: tCm } = useTranslation("camera-mapping");
  const { t: tShared } = useTranslation("shared");
  // PO QA 3차 y: 도면 라인 방향 라벨 (입/퇴장 등). countingLineEventType 키는 shared 네임스페이스.
  const lineLabelDefs = {
    default: {
      left: tShared("constants.countingLineEventType.default.left"),
      right: tShared("constants.countingLineEventType.default.right"),
    },
    visit: {
      left: tShared("constants.countingLineEventType.visit.left"),
      right: tShared("constants.countingLineEventType.visit.right"),
    },
    traffic: {
      left: tShared("constants.countingLineEventType.traffic.left"),
      right: tShared("constants.countingLineEventType.traffic.right"),
    },
  } as const;
  const lineLabelDefsRef = useRef(lineLabelDefs);
  lineLabelDefsRef.current = lineLabelDefs;
  const [deleteConfirm, setDeleteConfirm] = useState<
    { memoKind: "text" | "diagram"; memoId: number; floorPlanId: number } | null
  >(null);
  // 4-3-2: drawingTarget(기존 라인 보강) 또는 drawingRequest(신규) 둘 중 하나라도 활성이면 drawing 모드
  // drawingRequest 의 경우 다른 캔버스가 ownership 잡았으면 우리는 비활성
  const requestActiveForUs = !!drawingRequest && drawingActiveCanvas !== "snapshot";
  const isDrawing = !!drawingTarget || requestActiveForUs;
  const [memoCreateMode, setMemoCreateMode] = useState<"text" | "diagram" | null>(null);
  const [showReferenceLabels, setShowReferenceLabels] = useState(false);
  const panningEnabledRef = useRef(true);
  panningEnabledRef.current = !isDrawing && memoCreateMode === null;

  const { fabricRef, canvasRef, containerRef, isReady, zoomIn, zoomOut, percentToPixel, pixelToPercent, getBackgroundScaledWidth, bgFitVersion } =
    useFloorPlanCanvas({ imageUrl, panningEnabledRef, topPadding, bottomPadding, verticalAlign });

  // Stable refs for callbacks used in canvas event handlers
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const onFloorPlanDrawingCompleteRef = useRef(onFloorPlanDrawingComplete);
  onFloorPlanDrawingCompleteRef.current = onFloorPlanDrawingComplete;
  const onFloorPlanLineCoordinatesChangeRef = useRef(onFloorPlanLineCoordinatesChange);
  onFloorPlanLineCoordinatesChangeRef.current = onFloorPlanLineCoordinatesChange;
  const onFloorPlanZoneCoordinatesChangeRef = useRef(onFloorPlanZoneCoordinatesChange);
  onFloorPlanZoneCoordinatesChangeRef.current = onFloorPlanZoneCoordinatesChange;
  const onRequestFloorPlanDeleteRef = useRef(onRequestFloorPlanDelete);
  onRequestFloorPlanDeleteRef.current = onRequestFloorPlanDelete;
  const selectedEventSourceRef = useRef(selectedEventSource);
  selectedEventSourceRef.current = selectedEventSource;
  const drawingTargetRef = useRef(drawingTarget);
  drawingTargetRef.current = drawingTarget;
  // 4-3-2: drawingRequest / claim refs
  const drawingRequestRef = useRef(drawingRequest);
  drawingRequestRef.current = drawingRequest;
  const onFloorPlanDrawingRequestCompleteRef = useRef(onFloorPlanDrawingRequestComplete);
  onFloorPlanDrawingRequestCompleteRef.current = onFloorPlanDrawingRequestComplete;
  const drawingActiveCanvasRef = useRef(drawingActiveCanvas);
  drawingActiveCanvasRef.current = drawingActiveCanvas;
  const onDrawingClaimRef = useRef(onDrawingClaim);
  onDrawingClaimRef.current = onDrawingClaim;
  const onCameraMarkerClickRef = useRef(onCameraMarkerClick);
  onCameraMarkerClickRef.current = onCameraMarkerClick;
  const onReferenceShapeClickRef = useRef(onReferenceShapeClick);
  onReferenceShapeClickRef.current = onReferenceShapeClick;
  const camerasRef = useRef(cameras);
  camerasRef.current = cameras;

  // Track shape objects on canvas for cleanup
  const shapeObjectsRef = useRef<FabricObject[]>([]);
  // PO QA 8차 bf: 다른 카메라 이벤트소스(참조용) 객체 — 선택 카메라 shape 와 분리해 독립 cleanup
  const referenceObjectsRef = useRef<FabricObject[]>([]);
  const suppressSelectionClearedRef = useRef(false);

  // Drawing state
  const drawingStateRef = useRef<DrawingState | null>(null);

  // F19: zone sub-mode (polygon vs rectangle) — ref for sync read in canvas event handlers
  // 기본값은 사각형 — SnapshotCanvas 와 동일 기준 (사각형 구역이 훨씬 잦다). M 으로 다각형 전환.
  const zoneSubModeRef = useRef<"polygon" | "rectangle">("rectangle");
  const [zoneSubMode, setZoneSubMode] = useState<"polygon" | "rectangle">("rectangle");
  // F19: shift key tracking — ref for sync read in canvas event handlers
  const shiftHeldRef = useRef(false);

  // Coordinate editing mode state
  const activeCoordinateSgRef = useRef<ShapeGroup | null>(null);
  const skipRebuildLineIdsRef = useRef<Set<number>>(new Set());
  const skipRebuildZoneIdsRef = useRef<Set<number>>(new Set());

  // 6차 an: enter 직후 호출 — 부모 finishRef 에 자기 finishEdit 클로저 세팅. 다른 캔버스 클릭 시 호출되어 cross-canvas 자동 종료.
  const setupCrossCanvasFinish = (sg: ShapeGroup) => {
    coordinateEditingFinishRef.current = () => {
      const canvas = fabricRef.current;
      // 캔버스가 이미 사라졌어도 편집 상태는 반드시 푼다 —
      // 여기서 그냥 return 하면 ref 가 non-null 로 남아 저장 버튼이 계속 잠긴다
      if (canvas) {
        if (sg.type === "line") {
          finishLineCoordinateEdit(canvas, sg, shapeObjectsRef, skipRebuildLineIdsRef, onFloorPlanLineCoordinatesChangeRef, lineLabelDefsRef.current);
        } else if (sg.type === "zone") {
          finishZoneCoordinateEdit(canvas, sg, shapeObjectsRef, onFloorPlanZoneCoordinatesChangeRef);
        }
      }
      activeCoordinateSgRef.current = null;
      coordinateEditingFinishRef.current = null;
    };
  };

  // --- 겹친 카메라 순환 선택 (개선 니즈 #2 2단계) ---
  //
  // 카메라 매핑과 동일 규칙. fabric 의 findTarget 이 클릭·드래그·hover 의 대상을 모두 정하므로
  // 여기서 고르면 사이드바와 캔버스 마커가 따로 놀 수 없다. 후보가 여럿이면 dot 이 가까운 순으로
  // 세우고, 그중 지금 선택된 카메라가 있으면 그 다음 것을 잡는다 → 같은 자리를 반복해 누르면
  // 아래 깔린 카메라까지 차례로 닿는다.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const target = canvas;
    type FindTarget = (e: TPointerEvent) => ReturnType<typeof target.findTarget>;
    const original = target.findTarget.bind(target) as FindTarget;

    const patched: FindTarget = (e) => {
      const info = original(e);
      const active = target.getActiveObject();
      // 활성 객체의 컨트롤(회전 핸들 등)을 잡은 경우는 fabric 판단을 그대로 둔다
      if (active?.findControl?.(target.getViewportPoint(e))) return info;

      const currentCameraId = active instanceof CameraObject ? active.cameraId : null;
      const next = pickCameraAt(target, target.getScenePoint(e), currentCameraId);
      if (!next || next === info.target) return info;

      return { ...info, target: next, currentTarget: next };
    };

    target.findTarget = patched;
    return () => {
      target.findTarget = original;
    };
  }, [fabricRef, isReady]);

  // 편집 중 언마운트되면 finish 가 안 불려 편집 결과가 날아가고,
  // 부모의 coordinateEditingFinishRef 가 non-null 로 남아 저장 버튼이 잠긴 채 유지된다.
  useEffect(() => () => {
    if (activeCoordinateSgRef.current) coordinateEditingFinishRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render shapes (lines / zones) on canvas
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    // Preserve objects that should skip rebuild (after coordinate editing)
    const preservedObjects = shapeObjectsRef.current.filter((obj) => {
      const sg = getShapeGroup(obj);
      if (!sg) return false;
      if (sg.type === "line" && skipRebuildLineIdsRef.current.has(sg.id)) return true;
      if (sg.type === "zone" && skipRebuildZoneIdsRef.current.has(sg.id)) return true;
      return false;
    });
    skipRebuildLineIdsRef.current.clear();
    skipRebuildZoneIdsRef.current.clear();

    // Remove previous shape objects (suppress selection:cleared during cleanup)
    suppressSelectionClearedRef.current = true;
    for (const obj of shapeObjectsRef.current) {
      if (!preservedObjects.includes(obj)) {
        canvas.remove(obj);
      }
    }
    suppressSelectionClearedRef.current = false;
    shapeObjectsRef.current = [...preservedObjects];

    if (!showLineZone) {
      for (const obj of preservedObjects) canvas.remove(obj);
      shapeObjectsRef.current = [];
      canvas.requestRenderAll();
      return;
    }

    // --- Counting Lines ---
    const linesToRender = countingLines.filter(
      (l) => l.floorPlanConfig && l.floorPlanConfig.coordinates.length >= 2,
    );
    for (const line of linesToRender) {
      if (preservedObjects.some((o) => getShapeGroup(o)?.type === "line" && getShapeGroup(o)?.id === line.id)) continue;
      const coords = line.floorPlanConfig!.coordinates;
      const p0 = percentToPixel(coords[0]);
      const p1 = percentToPixel(coords[1]);

      const cx = (p0.x + p1.x) / 2;
      const cy = (p0.y + p1.y) / 2;
      const rp0 = { x: p0.x - cx, y: p0.y - cy };
      const rp1 = { x: p1.x - cx, y: p1.y - cy };

      const polyline = new Polyline([rp0, rp1], {
        stroke: LINE_STROKE,
        strokeWidth: LINE_STROKE_WIDTH,
        strokeUniform: true,
        fill: "transparent",
        selectable: false,
        evented: false,
        objectCaching: false,
      });

      // r ②: 라인이 라벨보다 짧거나 비슷하면 라벨을 숨김 (라인을 가리지 않도록)
      const dxx = p1.x - p0.x;
      const dyy = p1.y - p0.y;
      const lineLen = Math.sqrt(dxx * dxx + dyy * dyy);
      const dyn = getDynamicLineLabelStyle(canvas);
      const nameLabel = createLabelGroup(
        line.analysisConfig?.name ?? line.key,
        0,
        0,
        { ...LINE_NAME_LABEL_STYLE, ...dyn },
      );
      const showLabel = lineLen > (nameLabel.width ?? 0) * 1.2;
      // (F12) 도면에서는 입/퇴장 방향 라벨 제거 — 스냅샷에서만 노출
      const groupChildren: FabricObject[] = showLabel
        ? [polyline, nameLabel]
        : [polyline];

      const group = new Group(groupChildren, {
        left: cx,
        top: cy,
        originX: "center",
        originY: "center",
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        borderColor: BORDER_COLOR,
        borderScaleFactor: 2,
        cornerColor: CORNER_COLOR,
        cornerStrokeColor: CORNER_STROKE_COLOR,
        cornerStyle: "rect",
        cornerSize: 8,
        transparentCorners: false,
        lockScalingFlip: true,
        objectCaching: false,
        subTargetCheck: false,
      });

      const shapeGroup: LineShapeGroup = {
        type: "line",
        id: line.id,
        editingMode: "size",
        lineName: line.analysisConfig?.name ?? line.key,
        eventType: (line.analysisConfig?.eventType ?? "default") as import("./floor-plan-viewer/constants").CountingLineEventType,
        fabricGroup: group,
        coordinateParts: null,
      };
      setShapeGroup(group, shapeGroup);

      canvas.add(group);
      shapeObjectsRef.current.push(group);
    }

    // --- Interval Zones ---
    const zonesToRender = intervalZones.filter(
      (z) => z.floorPlanConfig && z.floorPlanConfig.coordinates.length >= 3,
    );
    for (const zone of zonesToRender) {
      if (preservedObjects.some((o) => getShapeGroup(o)?.type === "zone" && getShapeGroup(o)?.id === zone.id)) continue;
      const coords = zone.floorPlanConfig!.coordinates;
      const pts = coords.map((c) => percentToPixel(c));

      const centroidX = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const centroidY = pts.reduce((s, p) => s + p.y, 0) / pts.length;

      const relPts = pts.map((p) => ({ x: p.x - centroidX, y: p.y - centroidY }));
      const poly = new Polygon(relPts, {
        originX: "center",
        originY: "center",
        fill: ZONE_FILL,
        stroke: ZONE_STROKE,
        strokeWidth: 2,
        strokeUniform: true,
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        borderColor: BORDER_COLOR,
        borderScaleFactor: 2,
        cornerColor: CORNER_COLOR,
        cornerStrokeColor: CORNER_STROKE_COLOR,
        cornerStyle: "rect",
        cornerSize: 8,
        transparentCorners: false,
        lockScalingFlip: true,
        objectCaching: false,
      });

      poly.set({ left: centroidX + poly.pathOffset.x, top: centroidY + poly.pathOffset.y });
      poly.setCoords();

      const shapeGroup: ZoneShapeGroup = {
        type: "zone",
        id: zone.id,
        editingMode: "size",
        zoneName: zone.analysisConfig?.name ?? zone.key,
        polygon: poly,
        coordinateParts: null,
      };
      setShapeGroup(poly, shapeGroup);

      // r: 구역 라벨도 도면 bg 비례 동적 fontSize + collision 숨김
      const dynZone = getDynamicLineLabelStyle(canvas);
      const nameLabel = createLabelGroup(
        zone.analysisConfig?.name ?? zone.key,
        centroidX,
        centroidY,
        { ...ZONE_LABEL_STYLE, ...dynZone },
      );
      setShapeGroup(nameLabel, shapeGroup);

      // 구역 바운딩 박스가 라벨보다 작으면 라벨 숨김 (canvas에는 항상 추가, visible만 토글)
      const polyW = poly.width ?? 0;
      const polyH = poly.height ?? 0;
      const labelW = nameLabel.width ?? 0;
      const labelH = nameLabel.height ?? 0;
      const showZoneLabel = polyW > labelW * 1.2 && polyH > labelH * 1.2;
      if (!showZoneLabel) nameLabel.set({ visible: false });

      canvas.add(poly);
      canvas.add(nameLabel);
      shapeObjectsRef.current.push(poly, nameLabel);
    }

    // Restore selection after rebuild
    const currentSelection = selectedEventSourceRef.current;
    if (currentSelection) {
      const target = shapeObjectsRef.current.find((obj) => {
        const sg = getShapeGroup(obj);
        return sg && sg.type === currentSelection.type && sg.id === currentSelection.id && obj.selectable;
      });
      if (target) {
        canvas.setActiveObject(target);
      }
    }

    canvas.requestRenderAll();
  }, [countingLines, intervalZones, showLineZone, isReady, fabricRef, percentToPixel, bgFitVersion]);

  // --- 참조 레이어: 같은 도면 다른 카메라의 이벤트소스 (흐리게·비인터랙티브) — PO QA 8차 bf ---
  // 선택 카메라 shape 와 완전히 분리(별도 ref·effect)해서 선택/편집/드로잉 로직에 영향 없음.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    for (const obj of referenceObjectsRef.current) canvas.remove(obj);
    referenceObjectsRef.current = [];

    if (!showLineZone) {
      canvas.requestRenderAll();
      return;
    }

    // F14: 카메라 미선택 시 전체 오버뷰이므로 더 선명하게 표시
    const REF_OPACITY = selectedCameraId == null ? 1 : 0.3;

    const refLines = referenceCountingLines.filter(
      (l) => l.floorPlanConfig && l.floorPlanConfig.coordinates.length >= 2,
    );
    for (const line of refLines) {
      const coords = line.floorPlanConfig!.coordinates;
      const p0 = percentToPixel(coords[0]);
      const p1 = percentToPixel(coords[1]);
      const cx = (p0.x + p1.x) / 2;
      const cy = (p0.y + p1.y) / 2;
      const polyline = new Polyline(
        [
          { x: p0.x - cx, y: p0.y - cy },
          { x: p1.x - cx, y: p1.y - cy },
        ],
        {
          stroke: LINE_STROKE,
          strokeWidth: LINE_STROKE_WIDTH,
          strokeUniform: true,
          fill: "transparent",
          objectCaching: false,
        },
      );

      if (selectedCameraId == null && showReferenceLabels) {
        const dxx = p1.x - p0.x;
        const dyy = p1.y - p0.y;
        const lineLen = Math.sqrt(dxx * dxx + dyy * dyy);
        const dyn = getDynamicLineLabelStyle(canvas);
        const nameLabel = createLabelGroup(
          line.analysisConfig?.name ?? line.key,
          0,
          0,
          { ...LINE_NAME_LABEL_STYLE, ...dyn },
        );
        const showLabel = lineLen > (nameLabel.width ?? 0) * 1.2;
        const groupChildren: FabricObject[] = showLabel
          ? [polyline, nameLabel]
          : [polyline];

        const group = new Group(groupChildren, {
          left: cx,
          top: cy,
          originX: "center",
          originY: "center",
          opacity: REF_OPACITY,
          selectable: false,
          evented: true,
          hoverCursor: "pointer",
          objectCaching: false,
        });
        (group as TaggedFabricObject).__refTarget = { cameraId: line.cameraId, type: "line", id: line.id };
        canvas.add(group);
        canvas.sendObjectToBack(group);
        referenceObjectsRef.current.push(group);
      } else {
        polyline.set({
          left: cx,
          top: cy,
          originX: "center",
          originY: "center",
          opacity: REF_OPACITY,
          selectable: false,
          evented: true,
          hoverCursor: "pointer",
        });
        (polyline as TaggedFabricObject).__refTarget = { cameraId: line.cameraId, type: "line", id: line.id };
        canvas.add(polyline);
        canvas.sendObjectToBack(polyline);
        referenceObjectsRef.current.push(polyline);
      }
    }

    const refZones = referenceIntervalZones.filter(
      (z) => z.floorPlanConfig && z.floorPlanConfig.coordinates.length >= 3,
    );
    for (const zone of refZones) {
      const coords = zone.floorPlanConfig!.coordinates;
      const pts = coords.map((c) => percentToPixel(c));
      const cxx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const cyy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      const relPts = pts.map((p) => ({ x: p.x - cxx, y: p.y - cyy }));
      const poly = new Polygon(relPts, {
        originX: "center",
        originY: "center",
        fill: ZONE_FILL,
        stroke: ZONE_STROKE,
        strokeWidth: 2,
        strokeUniform: true,
        opacity: REF_OPACITY,
        selectable: false,
        evented: true,
        hoverCursor: "pointer",
        objectCaching: false,
      });
      (poly as TaggedFabricObject).__refTarget = { cameraId: zone.cameraId, type: "zone", id: zone.id };
      poly.set({ left: cxx + poly.pathOffset.x, top: cyy + poly.pathOffset.y });
      poly.setCoords();
      canvas.add(poly);
      canvas.sendObjectToBack(poly);
      referenceObjectsRef.current.push(poly);

      if (selectedCameraId == null && showReferenceLabels) {
        const dynZone = getDynamicLineLabelStyle(canvas);
        const nameLabel = createLabelGroup(
          zone.analysisConfig?.name ?? zone.key,
          cxx,
          cyy,
          { ...ZONE_LABEL_STYLE, ...dynZone },
        );
        nameLabel.set({ selectable: false, evented: false });
        const polyW = poly.width ?? 0;
        const polyH = poly.height ?? 0;
        const labelW = nameLabel.width ?? 0;
        const labelH = nameLabel.height ?? 0;
        const showZoneLabel = polyW > labelW * 1.2 && polyH > labelH * 1.2;
        if (!showZoneLabel) nameLabel.set({ visible: false });
        canvas.add(nameLabel);
        canvas.sendObjectToBack(nameLabel);
        referenceObjectsRef.current.push(nameLabel);
      }
    }

    // 참조 객체는 항상 배경 바로 위, 선택 카메라 shape·마커 아래
    const bg = getBgImage(canvas);
    if (bg) canvas.sendObjectToBack(bg);

    canvas.requestRenderAll();
  }, [referenceCountingLines, referenceIntervalZones, showLineZone, isReady, fabricRef, percentToPixel, bgFitVersion, selectedCameraId, showReferenceLabels]);

  // --- 도면 카메라 마커 렌더링 (4-1-1, read-only) ---
  // 카메라 매핑 페이지에서 배치된 카메라들을 도면 위에 표시
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    // 기존 카메라 마커 모두 제거 후 다시 추가 (간결한 동기화)
    const existing = canvas.getObjects().filter((o): o is CameraObject => o instanceof CameraObject);
    existing.forEach((o) => canvas.remove(o));

    const bgScaledWidth = getBackgroundScaledWidth();
    const iconScale = bgScaledWidth > 0 ? resizeCameraIconScale(iconSizeLocal, bgScaledWidth) : 1;
    // PO QA 2차 r: 분석 초안 도면은 read-only 뷰라 마커 작아질 때 라벨도 같이 축소하되,
    // 라벨은 오브젝트 스케일을 역적용해 그리므로(CameraObject._render) 화면상 크기가 iconScale 과 무관.
    // 이 값만이 라벨을 줄이는 유일한 장치 → 하한이 높으면 도면이 작아져도 라벨이 안 따라 줄어든다.
    // 하한 0.75 일 때 [둘 다] 모드(iconScale≈0.47)에서 라벨이 도면 대비 1.6배로 남아 도면을 가림.
    // 0.4 로 낮춰 [둘 다] 모드에서도 도면 축소 비율을 그대로 따라가게 함.
    // labelSizeLocal(사용자가 정한 도면별 배수)은 그 위에 곱한다 — 자동 축소를 덮어쓰지 않도록.
    const labelFontScale = Math.max(0.4, Math.min(1, iconScale)) * labelSizeLocal;

    cameras.forEach((camera) => {
      if (!camera.position) return;
      // adjustment 가 있으면 조정 위치, 없으면 원본 위치
      const usePos = camera.adjustmentPosition ?? camera.position;
      const px = percentToPixel({ x: usePos.x, y: usePos.y });
      const displayNumber = camera.analysisNumber ?? cameraDisplayNames?.get(camera.id) ?? null;
      const cameraType = camera.adjustmentPosition
        ? "edit"
        : camera.mappingType === "add"
          ? "add"
          : "default";
      // 색만으로 추가/조정을 구분하면 흑백 출력·색각 이상에서 단서가 사라짐 → 카메라 매핑과 같은 접미사 병기
      const cameraIdPrefix =
        cameraType === "add"
          ? tCm("card.added")
          : cameraType === "edit"
            ? tCm("card.adjusted")
            : null;
      const obj = new CameraObject({
        cameraId: camera.id,
        cameraType,
        displayNumber,
        cameraIdPrefix,
        left: px.x,
        top: px.y,
        angle: usePos.rotation ?? 0,
        iconScale,
        // 6차 at: 선택된 카메라 또는 그 카메라의 이벤트소스 선택 시 focused 색상 (카메라 매핑 페이지와 동일 select UI)
        isFocused: camera.id === selectedCameraId,
        labelFontScale,
      });
      // read-only — 드래그/리사이즈 불가, 클릭만 허용 (7차 az)
      obj.selectable = false;
      obj.evented = true;
      obj.hasControls = false;
      obj.hasBorders = false;
      obj.lockMovementX = true;
      obj.lockMovementY = true;
      obj.lockRotation = true;
      obj.hoverCursor = "pointer";
      canvas.add(obj);
      // (F12) 카메라 마커를 라인/구역 아래로 — 겹칠 때 라인/구역 선택 우선
      canvas.sendObjectToBack(obj);
    });
    // bg 이미지를 맨 뒤로 복원
    const bg = canvas.getObjects().find((o) => (o as { __isFloorPlanBg?: boolean }).__isFloorPlanBg);
    if (bg) canvas.sendObjectToBack(bg);

    canvas.requestRenderAll();
    // 드래그 중 실시간 반영을 위해 prop(cameraIconSize) 이 아니라 로컬 상태를 의존해야 한다.
    // prop 을 의존하면 저장 후 refetch 시점에야 다시 그려져 "놓아야 보이는" 상태가 됨.
  }, [cameras, selectedCameraId, cameraDisplayNames, iconSizeLocal, labelSizeLocal, isReady, fabricRef, percentToPixel, getBackgroundScaledWidth, bgFitVersion, tCm]);

  // --- 7차 az: 도면 카메라 마커 클릭 → 카메라 선택 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const refOf = (t: FabricObject | undefined) =>
      t ? (t as TaggedFabricObject).__refTarget : undefined;
    // 그리기/좌표편집 중엔 참조 네비게이션·hover 비활성 (기존 편집 흐름 보호)
    const isEditing = () =>
      !!drawingTargetRef.current || !!drawingRequestRef.current || !!activeCoordinateSgRef.current;

    // 참조 구역은 "링크" 성격 — 클릭=그 카메라로 이동, 드래그=패닝(도면 밀기).
    // 눌렀을 땐 클릭/드래그를 알 수 없으므로 mouse:up 에서 이동량으로 판정.
    const CLICK_MOVE_THRESHOLD = 8; // px — 이보다 덜 움직였으면 '클릭'
    let pendingRefClick:
      | { ref: { cameraId: number; type: "line" | "zone"; id: number }; x: number; y: number }
      | null = null;

    const handleMouseDown = (opt: TPointerEventInfo<TPointerEvent>) => {
      pendingRefClick = null;
      if ((opt.e as MouseEvent).button !== 0) return;
      const target = opt.target;
      if (!target) return;
      // 카메라 마커 클릭 → 카메라 선택 (기존 동작 유지)
      if (target instanceof CameraObject) {
        const camera = camerasRef.current.find((c) => c.id === target.cameraId);
        if (camera) onCameraMarkerClickRef.current?.(camera);
        return;
      }
      // 참조(다른 카메라) 라인/구역 — 클릭이면 mouse:up 에서 이동, 드래그면 패닝(useFloorPlanCanvas)
      const ref = refOf(target);
      if (ref && !isEditing()) {
        const e = opt.e as MouseEvent;
        pendingRefClick = { ref, x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = (opt: TPointerEventInfo<TPointerEvent>) => {
      const pending = pendingRefClick;
      pendingRefClick = null;
      if (!pending || isEditing()) return;
      const e = opt.e as MouseEvent;
      const moved = Math.hypot(e.clientX - pending.x, e.clientY - pending.y);
      // 거의 안 움직였으면 클릭 → 소속 스냅샷으로 이동 + 구역 포커싱 (v1 복원)
      if (moved <= CLICK_MOVE_THRESHOLD) onReferenceShapeClickRef.current?.(pending.ref);
    };

    // hover 강조 — 클릭 가능함을 옅게 표시 (참조 shape 만)
    const hoverBaseOpacity = new WeakMap<FabricObject, number>();
    const handleMouseOver = (opt: TPointerEventInfo<TPointerEvent>) => {
      const target = opt.target;
      if (!target || !refOf(target) || isEditing()) return;
      if (!hoverBaseOpacity.has(target)) hoverBaseOpacity.set(target, target.opacity ?? 1);
      target.set({ opacity: Math.min(1, (hoverBaseOpacity.get(target) ?? 1) + 0.4) });
      canvas.requestRenderAll();
    };
    const handleMouseOut = (opt: TPointerEventInfo<TPointerEvent>) => {
      const target = opt.target;
      if (!target || !hoverBaseOpacity.has(target)) return;
      target.set({ opacity: hoverBaseOpacity.get(target)! });
      hoverBaseOpacity.delete(target);
      canvas.requestRenderAll();
    };

    canvas.on("mouse:down", handleMouseDown);
    canvas.on("mouse:up", handleMouseUp);
    canvas.on("mouse:over", handleMouseOver);
    canvas.on("mouse:out", handleMouseOut);
    return () => {
      canvas.off("mouse:down", handleMouseDown);
      canvas.off("mouse:up", handleMouseUp);
      canvas.off("mouse:over", handleMouseOver);
      canvas.off("mouse:out", handleMouseOut);
    };
  }, [fabricRef]);

  // --- 도면 메모(다이어그램·텍스트) 렌더링 (read-only — 분석 초안에선 표시만) ---
  // PO QA 8차: bgFitVersion 변경(topPadding·리사이즈 등) 시 배경이 이동하면 메모 위치도 재계산 필요.
  // 기존 오브젝트 전부 제거 후 데이터 기반으로 재생성해 percentToPixel 좌표를 다시 계산한다.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const ctx = { percentToPixel, pixelToPercent };
    const editable = false;

    // 기존 도면 메모 오브젝트 전부 제거 후 재생성
    const existing = canvas.getObjects().filter(isFloorPlanMemoObject);
    for (const obj of existing) {
      canvas.remove(obj as never);
    }

    (floorPlanMemoDiagrams ?? []).forEach((memo) => {
      canvas.add(createFloorPlanMemoDiagramObject(memo, ctx, { editable }));
    });
    (floorPlanMemoTexts ?? []).forEach((memo) => {
      canvas.add(createFloorPlanMemoTextObject(memo, ctx, { editable }));
    });

    // 메모는 배경 바로 위 (라인·존·카메라 아래)
    canvas.getObjects().forEach((obj) => {
      if (isFloorPlanMemoObject(obj)) canvas.sendObjectToBack(obj);
    });
    const bg = canvas.getObjects().find((o) => (o as { __isFloorPlanBg?: boolean }).__isFloorPlanBg);
    if (bg) canvas.sendObjectToBack(bg);
    canvas.requestRenderAll();
  }, [isReady, floorPlanMemoDiagrams, floorPlanMemoTexts, fabricRef, percentToPixel, pixelToPercent, bgFitVersion]);

  // --- 도면 메모 편집 이벤트 (이동 / 텍스트 수정 → mutation) ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const handleModified = (e: { target?: unknown }) => {
      const target = e.target;
      if (!target || !isFloorPlanMemoObject(target)) return;

      if (target.__memoKind === "diagram") {
        const diagram = target as TaggedDiagram;
        const newWidth = diagram.getScaledWidth();
        const newHeight = diagram.getScaledHeight();
        diagram.set({ width: newWidth, height: newHeight, scaleX: 1, scaleY: 1 });
        diagram.setCoords();
        const coords = extractDiagramCoordinates(diagram, pixelToPercent);
        onMemoDiagramModified?.(
          diagram.__memoId,
          diagram.__floorPlanId,
          coords,
          diagram.__style,
        );
      } else if (target.__memoKind === "text") {
        const text = target as TaggedText;
        const position = extractTextPosition(text, pixelToPercent);
        const newWidth = (text.width ?? text.__style.width) as number;
        onMemoTextModified?.(
          text.__memoId,
          text.__floorPlanId,
          text.text,
          position,
          { ...text.__style, width: newWidth },
        );
      }
    };

    canvas.on("object:modified", handleModified);
    return () => {
      canvas.off("object:modified", handleModified);
    };
  }, [isReady, fabricRef, pixelToPercent, onMemoDiagramModified, onMemoTextModified]);

  // --- 도면 메모 신규 생성 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady || !memoCreateMode || currentFloorPlanId == null) return;

    canvas.defaultCursor = "crosshair";
    canvas.selection = false;

    let dragStart: { x: number; y: number } | null = null;
    let dragRect: Rect | null = null;

    const handleDown = (opt: TPointerEventInfo<TPointerEvent>) => {
      const ptr = { x: opt.scenePoint.x, y: opt.scenePoint.y };
      if (memoCreateMode === "text") {
        const pos = pixelToPercent({ x: ptr.x, y: ptr.y });
        const tempId = onMemoTextCreate?.(currentFloorPlanId, pos);
        if (tempId != null) {
          const obj = createFloorPlanMemoTextObject(
            { id: tempId, floorPlanId: currentFloorPlanId, content: t("floorPlanMemo.defaultTextContent", { ns: "shared" }), position: pos, style: DEFAULT_TEXT_STYLE_FPV },
            { percentToPixel, pixelToPercent },
            { editable: true },
          );
          canvas.add(obj);
          canvas.requestRenderAll();
        }
        setMemoCreateMode(null);
      } else if (memoCreateMode === "diagram") {
        dragStart = { x: ptr.x, y: ptr.y };
      }
    };

    const handleMove = (opt: TPointerEventInfo<TPointerEvent>) => {
      if (memoCreateMode !== "diagram" || !dragStart) return;
      const ptr = { x: opt.scenePoint.x, y: opt.scenePoint.y };
      const left = Math.min(dragStart.x, ptr.x);
      const top = Math.min(dragStart.y, ptr.y);
      const width = Math.abs(ptr.x - dragStart.x);
      const height = Math.abs(ptr.y - dragStart.y);
      if (!dragRect) {
        dragRect = new Rect({
          left, top, width, height,
          fill: "rgba(72, 75, 210, 0.12)",
          stroke: "#484BD2",
          strokeWidth: 2,
          selectable: false, evented: false, objectCaching: false,
        });
        canvas.add(dragRect);
      } else {
        dragRect.set({ left, top, width, height });
      }
      canvas.requestRenderAll();
    };

    const handleUp = (opt: TPointerEventInfo<TPointerEvent>) => {
      if (memoCreateMode !== "diagram" || !dragStart) return;
      const ptr = { x: opt.scenePoint.x, y: opt.scenePoint.y };
      const left = Math.min(dragStart.x, ptr.x);
      const top = Math.min(dragStart.y, ptr.y);
      const right = Math.max(dragStart.x, ptr.x);
      const bottom = Math.max(dragStart.y, ptr.y);
      if (right - left >= 4 && bottom - top >= 4) {
        const pts = [
          pixelToPercent({ x: left, y: top }),
          pixelToPercent({ x: right, y: top }),
          pixelToPercent({ x: right, y: bottom }),
          pixelToPercent({ x: left, y: bottom }),
        ];
        const tempId = onMemoDiagramCreate?.(currentFloorPlanId, pts);
        if (tempId != null) {
          const obj = createFloorPlanMemoDiagramObject(
            { id: tempId, floorPlanId: currentFloorPlanId, coordinates: pts, style: DEFAULT_DIAGRAM_STYLE_FPV },
            { percentToPixel, pixelToPercent },
            { editable: true },
          );
          canvas.add(obj);
          canvas.requestRenderAll();
        }
      }
      if (dragRect) {
        canvas.remove(dragRect);
        dragRect = null;
      }
      dragStart = null;
      setMemoCreateMode(null);
    };

    canvas.on("mouse:down", handleDown);
    canvas.on("mouse:move", handleMove);
    canvas.on("mouse:up", handleUp);
    return () => {
      canvas.off("mouse:down", handleDown);
      canvas.off("mouse:move", handleMove);
      canvas.off("mouse:up", handleUp);
      if (dragRect) canvas.remove(dragRect);
      // 평소 grab — 모든 pan 가능 캔버스(매핑 도면 / 스냅샷 / 분석 초안 도면)와 통일
      canvas.defaultCursor = "grab";
      canvas.selection = true;
    };
  }, [memoCreateMode, isReady, fabricRef, pixelToPercent, currentFloorPlanId, onMemoTextCreate, onMemoDiagramCreate]);

  // --- 도면 메모 삭제: Delete/Backspace 키 ---
  // 단순 오타·실수로 손실되는 것을 막기 위해 확인 다이얼로그를 띄움 (PO QA 2차 n)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const active = canvas.getActiveObject();
      if (!active || !isFloorPlanMemoObject(active)) return;
      if (active.__memoKind === "text" && (active as { isEditing?: boolean }).isEditing) return;

      e.preventDefault();
      setDeleteConfirm({
        memoKind: active.__memoKind,
        memoId: active.__memoId,
        floorPlanId: active.__floorPlanId,
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isReady, fabricRef]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteConfirm) return;
    const canvas = fabricRef.current;
    if (canvas) {
      const target = canvas
        .getObjects()
        .find(
          (o) =>
            isFloorPlanMemoObject(o)
            && o.__memoId === deleteConfirm.memoId
            && o.__memoKind === deleteConfirm.memoKind,
        );
      if (target) {
        canvas.discardActiveObject();
        canvas.remove(target);
        canvas.requestRenderAll();
      }
    }
    if (deleteConfirm.memoKind === "diagram") {
      onMemoDiagramDelete?.(deleteConfirm.memoId, deleteConfirm.floorPlanId);
    } else {
      onMemoTextDelete?.(deleteConfirm.memoId, deleteConfirm.floorPlanId);
    }
    setDeleteConfirm(null);
  }, [deleteConfirm, fabricRef, onMemoDiagramDelete, onMemoTextDelete]);

  // --- Canvas selection events ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    function handleSelectionEvent(selected: FabricObject[] | undefined) {
      if (activeCoordinateSgRef.current) return;
      if (!selected || selected.length === 0) {
        onSelectionChangeRef.current?.(null);
        return;
      }
      const target = selected[0];
      const sg = getShapeGroup(target);
      if (sg?.type === "line" || sg?.type === "zone") {
        onSelectionChangeRef.current?.({ type: sg.type, id: sg.id });
      } else {
        onSelectionChangeRef.current?.(null);
      }
    }

    const onCreated = (opt: { selected: FabricObject[] }) => handleSelectionEvent(opt.selected);
    const onUpdated = (opt: { selected: FabricObject[] }) => handleSelectionEvent(opt.selected);
    const onCleared = () => {
      if (suppressSelectionClearedRef.current) return;
      if (activeCoordinateSgRef.current) return;
      onSelectionChangeRef.current?.(null);
    };

    // 클릭 도중 레이아웃 이동(구역 클릭 → 뷰 자동 전환 등)으로 캔버스 요소가 움직이면,
    // fabric 이 정지한 포인터를 요소 이동량만큼의 drag 로 오인해 object:modified 를 발화한다
    // (mousedown/mouseup 의 scene 좌표가 캔버스 offset 변화만큼 어긋나기 때문).
    // 화면(client) 기준 포인터 이동이 없으면 사용자 편집이 아니므로 좌표 반영을 차단한다.
    let downClient: { x: number; y: number } | null = null;
    const onMouseDownTrackClient = (opt: TPointerEventInfo) => {
      const e = opt.e as MouseEvent;
      downClient = typeof e.clientX === "number" ? { x: e.clientX, y: e.clientY } : null;
    };
    const isPhantomDrag = (opt: ModifiedEvent) => {
      const e = opt.e as MouseEvent | undefined;
      if (!e || typeof e.clientX !== "number" || !downClient) return false;
      return Math.hypot(e.clientX - downClient.x, e.clientY - downClient.y) < 3;
    };

    const onObjectModified = (opt: ModifiedEvent) => {
      const target = opt.target;
      if (!target) return;
      const sg = getShapeGroup(target);
      if (!sg) return;
      if (isPhantomDrag(opt)) return;

      const bgObj = getBgImage(canvas);
      const ns = getImageNaturalSize(canvas);

      if (sg.type === "line" && sg.editingMode === "size") {
        const lineGroup = sg.fabricGroup as Group;
        let worldPts = getLineGroupEndpoints(lineGroup);

        if (bgObj && ns) {
          const clamped = worldPts.map((p) => clampToImageBounds(p, bgObj, ns));
          const needsRebuild = worldPts.some((p, i) => p.x !== clamped[i].x || p.y !== clamped[i].y);
          if (needsRebuild) {
            worldPts = clamped;
            rebuildLineGroup(canvas, sg, worldPts, shapeObjectsRef, lineLabelDefsRef.current);
          }
        }

        const coords = worldPts.map((p) => getNormalizedPoint(pixelToPercentStatic(p, canvas)));
        onFloorPlanLineCoordinatesChangeRef.current?.(sg.id, coords);
      } else if (sg.type === "zone" && sg.editingMode === "size" && sg.polygon) {
        const poly = sg.polygon as Polygon;
        const matrix = poly.calcTransformMatrix();
        const poX = poly.pathOffset?.x ?? 0;
        const poY = poly.pathOffset?.y ?? 0;
        let worldPts = poly.points!.map((pt) => {
          const lx = pt.x - poX;
          const ly = pt.y - poY;
          return {
            x: lx * matrix[0] + ly * matrix[2] + matrix[4],
            y: lx * matrix[1] + ly * matrix[3] + matrix[5],
          };
        });

        if (bgObj && ns) {
          const clamped = worldPts.map((p) => clampToImageBounds(p, bgObj, ns));
          const needsRebuild = worldPts.some((p, i) => p.x !== clamped[i].x || p.y !== clamped[i].y);
          if (needsRebuild) {
            worldPts = clamped;
            rebuildZonePolygon(canvas, sg, worldPts, shapeObjectsRef);
          }
        }

        // 리사이즈 후 라벨 visibility 재평가 (zone 크기 변경 시 라벨 표시/숨김 갱신)
        const zoneLabel = shapeObjectsRef.current.find((o) => getShapeGroup(o) === sg && o !== sg.polygon);
        if (zoneLabel) {
          const effW = (poly.width ?? 0) * Math.abs(poly.scaleX ?? 1);
          const effH = (poly.height ?? 0) * Math.abs(poly.scaleY ?? 1);
          const lw = zoneLabel.width ?? 0;
          const lh = zoneLabel.height ?? 0;
          const shouldShow = effW > lw * 1.2 && effH > lh * 1.2;
          zoneLabel.set({ visible: shouldShow });
          // 라벨 위치도 갱신 (centroid)
          const cx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
          const cy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
          zoneLabel.set({ left: cx, top: cy });
          zoneLabel.setCoords();
        }

        const coords = worldPts.map((p) => getNormalizedPoint(pixelToPercentStatic(p, canvas)));
        onFloorPlanZoneCoordinatesChangeRef.current?.(sg.id, coords);
      }
    };

    // Clamp shapes within image bounds during drag & sync zone labels
    const onObjectMoving = (opt: { target: FabricObject }) => {
      const target = opt.target;
      if (!target || !getShapeGroup(target)) return;
      const sg = getShapeGroup(target)!;
      const bg = getBgImage(canvas);
      const ns = getImageNaturalSize(canvas);
      if (!bg || !ns) return;

      const scale = bg.scaleX!;
      const scaledW = ns.width * scale;
      const scaledH = ns.height * scale;
      const imgL = bg.left! - scaledW / 2;
      const imgT = bg.top! - scaledH / 2;
      const imgR = imgL + scaledW;
      const imgB = imgT + scaledH;

      target.setCoords();
      // 6차 ao: line 은 화살표/라벨까지 포함한 bbox 기준으로 clamp 하면 endpoint 가 가장자리에 못 닿음 (화살표 길이만큼 떨어짐).
      // → size 모드일 때 line endpoint / zone vertex 만으로 clamp 계산. 좌표편집 모드 (handle drag) 는 기존 bbox 유지.
      let minX: number, maxX: number, minY: number, maxY: number;
      if (sg.type === "line" && sg.editingMode === "size") {
        const eps = getLineGroupEndpoints(target as Group);
        minX = Math.min(eps[0].x, eps[1].x);
        maxX = Math.max(eps[0].x, eps[1].x);
        minY = Math.min(eps[0].y, eps[1].y);
        maxY = Math.max(eps[0].y, eps[1].y);
      } else if (sg.type === "zone" && sg.editingMode === "size" && sg.polygon) {
        const poly = sg.polygon as Polygon;
        const matrix = poly.calcTransformMatrix();
        const poX = poly.pathOffset?.x ?? 0;
        const poY = poly.pathOffset?.y ?? 0;
        const xs: number[] = []; const ys: number[] = [];
        for (const pt of poly.points!) {
          const lx = pt.x - poX;
          const ly = pt.y - poY;
          xs.push(lx * matrix[0] + ly * matrix[2] + matrix[4]);
          ys.push(lx * matrix[1] + ly * matrix[3] + matrix[5]);
        }
        minX = Math.min(...xs); maxX = Math.max(...xs);
        minY = Math.min(...ys); maxY = Math.max(...ys);
      } else {
        const { tl, tr, bl, br } = target.aCoords!;
        minX = Math.min(tl.x, tr.x, bl.x, br.x);
        maxX = Math.max(tl.x, tr.x, bl.x, br.x);
        minY = Math.min(tl.y, tr.y, bl.y, br.y);
        maxY = Math.max(tl.y, tr.y, bl.y, br.y);
      }

      let dx = 0, dy = 0;
      if (minX < imgL) dx = imgL - minX;
      else if (maxX > imgR) dx = imgR - maxX;
      if (minY < imgT) dy = imgT - minY;
      else if (maxY > imgB) dy = imgB - maxY;

      if (dx !== 0 || dy !== 0) {
        target.set({ left: target.left! + dx, top: target.top! + dy });
        target.setCoords();
      }

      // Zone: move sibling objects (label) together with polygon (size mode only)
      if (sg.type === "zone" && sg.editingMode === "size" && (target as TaggedFabricObject).__prevLeft != null) {
        const moveDx = target.left! - (target as TaggedFabricObject).__prevLeft!;
        const moveDy = target.top! - (target as TaggedFabricObject).__prevTop!;
        const siblings = shapeObjectsRef.current.filter(
          (o: FabricObject) => getShapeGroup(o) === sg && o !== target,
        );
        for (const sib of siblings) {
          sib.set({ left: sib.left! + moveDx, top: sib.top! + moveDy });
          sib.setCoords();
        }
      }
      (target as TaggedFabricObject).__prevLeft = target.left!;
      (target as TaggedFabricObject).__prevTop = target.top!;
    };

    // 6차 an: 좌표 편집 모드에서 편집 중인 도형 외부를 클릭하면 즉시 편집 종료 (단일 클릭). 스냅과 동일 패턴.
    // 자기 캔버스 안 잡고 있을 때 다른 캔버스(스냅) 가 잡고 있으면 cross-canvas 종료.
    const onMouseDownAutoFinish = (opt: TPointerEventInfo) => {
      if ((opt.e as MouseEvent).button !== 0) return;
      const activeSg = activeCoordinateSgRef.current;
      if (activeSg) {
        const clickedSg = opt.target ? getShapeGroup(opt.target) : null;
        const insideActive =
          clickedSg && clickedSg.type === activeSg.type && clickedSg.id === activeSg.id;
        if (insideActive) return;
        if (activeSg.type === "line") {
          finishLineCoordinateEdit(canvas, activeSg, shapeObjectsRef, skipRebuildLineIdsRef, onFloorPlanLineCoordinatesChangeRef, lineLabelDefsRef.current);
        } else if (activeSg.type === "zone") {
          finishZoneCoordinateEdit(canvas, activeSg, shapeObjectsRef, onFloorPlanZoneCoordinatesChangeRef);
        }
        activeCoordinateSgRef.current = null;
        coordinateEditingFinishRef.current = null;
        return;
      }
      // 자기 안 잡고 있지만 다른 캔버스가 잡고 있으면 그쪽 종료
      coordinateEditingFinishRef.current?.();
    };

    canvas.on("selection:created", onCreated);
    canvas.on("selection:updated", onUpdated);
    canvas.on("selection:cleared", onCleared);
    canvas.on("object:modified", onObjectModified);
    canvas.on("object:moving", onObjectMoving);
    canvas.on("mouse:down", onMouseDownAutoFinish);
    canvas.on("mouse:down", onMouseDownTrackClient);

    return () => {
      canvas.off("selection:created", onCreated);
      canvas.off("selection:updated", onUpdated);
      canvas.off("selection:cleared", onCleared);
      canvas.off("object:modified", onObjectModified);
      canvas.off("object:moving", onObjectMoving);
      canvas.off("mouse:down", onMouseDownAutoFinish);
      canvas.off("mouse:down", onMouseDownTrackClient);
    };
  }, [fabricRef]);

  // --- Keyboard: ESC (cancel drawing / deselect) + Delete/Backspace + M (F19 zone sub-mode toggle) ---
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isDrawing) {
          onDrawingCancel?.();
        } else {
          const canvas = fabricRef.current;
          if (canvas) {
            const activeSg = activeCoordinateSgRef.current;
            if (activeSg) {
              if (activeSg.type === "line") {
                finishLineCoordinateEdit(canvas, activeSg, shapeObjectsRef, skipRebuildLineIdsRef, onFloorPlanLineCoordinatesChangeRef, lineLabelDefsRef.current);
              } else if (activeSg.type === "zone") {
                finishZoneCoordinateEdit(canvas, activeSg, shapeObjectsRef, onFloorPlanZoneCoordinatesChangeRef);
              }
              activeCoordinateSgRef.current = null;
              coordinateEditingFinishRef.current = null;
            }
            canvas.discardActiveObject();
            canvas.requestRenderAll();
          }
        }
        return;
      }
      // F19: M key — toggle zone sub-mode (polygon ↔ rectangle). Use e.code for IME independence (한글 모드에서도 동작)
      if (e.code === "KeyM" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // 텍스트 입력 중이면 단축키 비활성 (Delete/Backspace 분기와 동일 가드)
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return;
        const activeType =
          drawingTarget?.type
          ?? (drawingRequest && drawingActiveCanvas !== "snapshot" ? drawingRequest.type : undefined);
        if (activeType === "zone") {
          const canvas = fabricRef.current;
          if (canvas) {
            // Clear any in-progress drawing on mode switch
            if (drawingStateRef.current) {
              clearDrawingTemp(canvas, drawingCtxRef.current);
            }
            // Toggle sub-mode
            const next = zoneSubModeRef.current === "polygon" ? "rectangle" : "polygon";
            zoneSubModeRef.current = next;
            setZoneSubMode(next);
            // Update cursor immediately (setCursor forces instant visual update)
            const cur = next === "rectangle" ? ZONE_RECT_DRAWING_CURSOR : ZONE_DRAWING_CURSOR;
            canvas.defaultCursor = cur;
            canvas.hoverCursor = cur;
            canvas.setCursor(cur);
            canvas.requestRenderAll();
          }
          e.preventDefault();
          return;
        }
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        if (target?.isContentEditable) return;
        const canvas = fabricRef.current;
        if (!canvas) return;
        const active = canvas.getActiveObject();
        if (!active) return;
        const sg = getShapeGroup(active);
        if (sg?.type === "line" || sg?.type === "zone") {
          onRequestFloorPlanDeleteRef.current?.(sg.type, sg.id);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isDrawing, onDrawingCancel, fabricRef, drawingTarget, drawingRequest, drawingActiveCanvas]);

  // F19: Shift key tracking for line snap
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { shiftHeldRef.current = e.shiftKey; };
    const onKeyUp = (e: KeyboardEvent) => { shiftHeldRef.current = e.shiftKey; };
    const onBlur = () => { shiftHeldRef.current = false; };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // --- Programmatic selection from sidebar ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    if (!selectedEventSource) {
      if (!activeCoordinateSgRef.current) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
      return;
    }

    const target = shapeObjectsRef.current.find((obj) => {
      const sg = getShapeGroup(obj);
      return sg && sg.type === selectedEventSource.type && sg.id === selectedEventSource.id && obj.selectable;
    });

    if (target) {
      canvas.setActiveObject(target);
      canvas.requestRenderAll();
    } else if (!activeCoordinateSgRef.current) {
      // PO QA 8차: 선택된 소스가 이 캔버스에 도형이 없으면(예: 도면 미배치) 직전 선택을 비움 — 엉뚱한 요소 잔류 방지.
      // suppress: discard 가 selection:cleared 로 전파돼 selectedEventSource 가 비워지는 핑퐁(플리커) 차단.
      suppressSelectionClearedRef.current = true;
      canvas.discardActiveObject();
      suppressSelectionClearedRef.current = false;
      canvas.requestRenderAll();
    }
  }, [selectedEventSource, fabricRef]);

  // Drawing context for helper functions
  const drawingCtxRef = useRef<DrawingContext>({
    drawingStateRef,
    drawingTargetRef,
    onCompleteRef: onFloorPlanDrawingCompleteRef,
    drawingRequestRef,
    onRequestCompleteRef: onFloorPlanDrawingRequestCompleteRef,
  });

  // --- Drawing mode: canvas events ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const ctx = drawingCtxRef.current;

    const onMouseDown = (opt: TPointerEventInfo) => {
      if ((opt.e as MouseEvent).button !== 0) return;
      // 4-3-2: drawingTarget(기존 라인 보강) 또는 drawingRequest(신규) 둘 중 하나가 활성
      const hasTarget = !!drawingTargetRef.current;
      const hasRequest = !!drawingRequestRef.current;
      if (!hasTarget && !hasRequest) return;
      // drawingRequest 케이스: 다른 캔버스가 ownership 잡았으면 무시 + 첫 클릭 시 claim
      if (hasRequest && !hasTarget) {
        if (drawingActiveCanvasRef.current === "snapshot") return;
        if (drawingActiveCanvasRef.current == null) {
          onDrawingClaimRef.current?.("floorPlan");
        }
      }
      // F19: zone + rectangle mode → rect drag
      const activeType = drawingTargetRef.current?.type ?? drawingRequestRef.current?.type;
      if (activeType === "zone" && zoneSubModeRef.current === "rectangle") {
        handleRectDrawingMouseDown(canvas, opt.scenePoint, ctx);
        return;
      }
      handleDrawingClick(canvas, opt.scenePoint, ctx, shiftHeldRef.current);
    };

    const onMouseMove = (opt: TPointerEventInfo) => {
      const hasActive = !!drawingTargetRef.current || !!drawingRequestRef.current;
      if (!hasActive) return;
      // F19: rect drag in progress
      if (drawingStateRef.current?.rectDragStart) {
        handleRectDrawingMouseMove(canvas, opt.scenePoint, ctx);
        return;
      }
      if (!drawingStateRef.current) return;
      handleDrawingMove(canvas, opt.scenePoint, ctx, shiftHeldRef.current);
    };

    // F19: mouse up for rectangle drawing
    const onMouseUp = (opt: TPointerEventInfo) => {
      if (drawingStateRef.current?.rectDragStart) {
        handleRectDrawingMouseUp(canvas, opt.scenePoint, ctx);
      }
    };

    const onDblClick = (opt: TPointerEventInfo) => {
      // Drawing mode: zone double-click to close (target 또는 request)
      const activeType = drawingTargetRef.current?.type ?? drawingRequestRef.current?.type;
      if (activeType === "zone") {
        handleDrawingDblClick(canvas, ctx);
        return;
      }

      // If already in coordinate mode, exit it
      const activeSg = activeCoordinateSgRef.current;
      if (activeSg) {
        if (activeSg.type === "line") {
          finishLineCoordinateEdit(canvas, activeSg, shapeObjectsRef, skipRebuildLineIdsRef, onFloorPlanLineCoordinatesChangeRef, lineLabelDefsRef.current);
        } else if (activeSg.type === "zone") {
          finishZoneCoordinateEdit(canvas, activeSg, shapeObjectsRef, onFloorPlanZoneCoordinatesChangeRef);
        }
        activeCoordinateSgRef.current = null;
        coordinateEditingFinishRef.current = null;
        return;
      }

      // Enter coordinate mode on double-click
      if (opt.target) {
        const sg = getShapeGroup(opt.target);
        if (sg?.type === "line" && sg.editingMode !== "coordinate") {
          enterLineCoordinateMode(canvas, sg, shapeObjectsRef, activeCoordinateSgRef, lineLabelDefsRef.current);
          setupCrossCanvasFinish(sg);
        } else if (sg?.type === "zone" && sg.editingMode !== "coordinate") {
          enterZoneCoordinateMode(canvas, sg, shapeObjectsRef, activeCoordinateSgRef);
          setupCrossCanvasFinish(sg);
        }
      }
    };

    canvas.on("mouse:down", onMouseDown);
    canvas.on("mouse:move", onMouseMove);
    canvas.on("mouse:up", onMouseUp);
    canvas.on("mouse:dblclick", onDblClick);

    return () => {
      canvas.off("mouse:down", onMouseDown);
      canvas.off("mouse:move", onMouseMove);
      canvas.off("mouse:up", onMouseUp);
      canvas.off("mouse:dblclick", onDblClick);
    };
  }, [fabricRef]);

  // --- Drawing mode cursor & cleanup ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // 4-3-2: drawingTarget (보강) 또는 drawingRequest (신규, 다른 캔버스가 안 잡았을 때만) 시 그리기 커서
    const activeType =
      drawingTarget?.type
      ?? (drawingRequest && drawingActiveCanvas !== "snapshot" ? drawingRequest.type : undefined);
    if (activeType) {
      // F19: zone drawing cursor depends on sub-mode (polygon vs rectangle)
      let cur: string;
      if (activeType === "zone" && zoneSubModeRef.current === "rectangle") {
        cur = ZONE_RECT_DRAWING_CURSOR;
      } else if (activeType === "zone") {
        cur = ZONE_DRAWING_CURSOR;
      } else {
        cur = LINE_DRAWING_CURSOR;
      }
      canvas.defaultCursor = cur;
      canvas.hoverCursor = cur;
      // PO QA 8차 bg+bj: 그리기 중에는 기존 객체(화각·이벤트소스)를 hit-test 대상에서 제외.
      // 안 그러면 카메라가 많거나 기존 이벤트소스 위에 그릴 때 그게 선택돼서 그리기를 방해함.
      // 그리기 핸들러는 opt.target 이 아니라 scenePoint 만 쓰므로 target 탐색을 꺼도 무방.
      canvas.skipTargetFind = true;
    } else {
      // 평소 grab — 모든 pan 가능 캔버스와 통일
      canvas.defaultCursor = "grab";
      canvas.hoverCursor = "move";
      // 그리기 종료 → 기존 객체 선택/편집 복원
      canvas.skipTargetFind = false;
      if (drawingStateRef.current) {
        clearDrawingTemp(canvas, drawingCtxRef.current);
      }
    }
    canvas.requestRenderAll();
  }, [drawingTarget, drawingRequest, drawingActiveCanvas, fabricRef]);

  if (!imageUrl) return null;

  return (
    <Container ref={containerRef} $extraBasis={extraBasis}>
      {floorPlans.length > 0 && onViewerFloorPlanChange && (
        <ViewSelect $top={Math.max(topPadding ?? 0, 8)}>
          <ViewSelectTrigger type="button" onClick={() => setViewSelectOpen((v) => !v)}>
            <span>{viewerFloorPlanName || t("floorPlan.viewSelect")}</span>
            <Icon icon={viewSelectOpen ? "ic_chevron_up" : "ic_chevron_down"} width={16} />
          </ViewSelectTrigger>
          {viewSelectOpen && (
            <ViewSelectMenu>
              {floorPlans.map((fp) => (
                <ViewSelectItem
                  key={fp.id}
                  type="button"
                  $selected={fp.id === viewerFloorPlanId}
                  onClick={() => { onViewerFloorPlanChange(fp.id); setViewSelectOpen(false); }}
                >
                  {fp.name}
                </ViewSelectItem>
              ))}
            </ViewSelectMenu>
          )}
        </ViewSelect>
      )}
      {currentFloorPlanId != null && (onMemoTextCreate || onMemoDiagramCreate) && (
        <MemoToolbar>
          {onMemoTextCreate && (
            <MemoToolButton
              type="button"
              $active={memoCreateMode === "text"}
              onClick={() => setMemoCreateMode((m) => (m === "text" ? null : "text"))}
              title={t("floorPlanMemo.addText")}
            >
              <Icon icon="ic_edit" width={18} fill="none" stroke="currentColor" />
            </MemoToolButton>
          )}
          {onMemoDiagramCreate && (
            <MemoToolButton
              type="button"
              $active={memoCreateMode === "diagram"}
              onClick={() => setMemoCreateMode((m) => (m === "diagram" ? null : "diagram"))}
              title={t("floorPlanMemo.addDiagram")}
            >
              <Icon icon="ic_area_zone" width={18} fill="none" stroke="currentColor" />
            </MemoToolButton>
          )}
        </MemoToolbar>
      )}
      {isDrawing && drawingTarget && (
        <DrawingBanner>
          <Trans
            i18nKey="drawingBanner.message"
            ns="analysis-draft"
            values={{ name: drawingTarget.name }}
            components={{
              1: (
                <strong
                  style={{ color: drawingTarget.type === "zone" ? ZONE_STROKE : undefined }}
                />
              ),
            }}
          />
          {drawingTarget.type === "line" && <>{t("drawingBanner.lineHint")}{t("drawingBanner.lineHintShift")}</>}
          {drawingTarget.type === "zone" && t(zoneSubMode === "rectangle" ? "drawingBanner.zoneHintRect" : "drawingBanner.zoneHint")}
          <CancelButton
            onClick={(e) => {
              e.stopPropagation();
              onDrawingCancel?.();
            }}
          >
            {t("drawingBanner.cancel")}
          </CancelButton>
        </DrawingBanner>
      )}
      {/* Fabric.js wraps <canvas> in its own div, breaking React's DOM tracking.
          Isolate it so conditional siblings don't trigger insertBefore errors. */}
      <div>
        <canvas ref={canvasRef} />
      </div>
      {selectedCameraId == null && (
        <LabelToggleWrap>
          <Tooltip
            trigger={
              <LabelToggle
                type="button"
                $active={showReferenceLabels}
                onClick={() => setShowReferenceLabels((v) => !v)}
              >
                {showReferenceLabels ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M16 5C16.6699 5 17.2699 5.32993 17.6299 5.83984L21.5889 11.4209C21.8349 11.7677 21.8352 12.2325 21.5889 12.5791L17.6299 18.1504C17.2699 18.6602 16.6699 19 16 19H5C4.46957 19 3.96101 18.7891 3.58594 18.4141C3.21086 18.039 3 17.5304 3 17V7C3 6.46957 3.21086 5.96101 3.58594 5.58594C3.96101 5.21086 4.46957 5 5 5H16Z" fill="currentColor"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M2.64252 3.63275C2.99494 3.28308 3.56386 3.28377 3.91498 3.6347L19.3652 19.0849C19.7162 19.436 19.7168 20.0049 19.3671 20.3574C19.0155 20.7117 18.4428 20.7133 18.0898 20.3603L16.6298 18.9003C16.4299 18.9603 16.2198 18.9999 15.9999 18.9999H4.99994C4.46953 18.9999 3.96093 18.7891 3.58588 18.414C3.21085 18.0389 2.99994 17.5303 2.99994 16.9999V6.99994C2.99995 6.50011 3.17006 6.07035 3.4599 5.73041L2.63959 4.9101C2.28674 4.55706 2.28825 3.98438 2.64252 3.63275ZM15.9999 4.99994C16.6698 4.99994 17.2698 5.32992 17.6298 5.83978L21.5878 11.4199C21.8343 11.7674 21.8335 12.2324 21.5859 12.579L18.9999 16.2001L7.83002 4.99994H15.9999Z" fill="currentColor"/>
                  </svg>
                )}
              </LabelToggle>
            }
            side="top"
            sideOffset={4}
          >
            {showReferenceLabels ? t("floorPlanViewer.hideEventLabels") : t("floorPlanViewer.showEventLabels")}
          </Tooltip>
        </LabelToggleWrap>
      )}
      <ZoomButtons>
        {onScaleCommit && (
          <Tooltip
            trigger={
              <ZoomButton onClick={() => setShowScalePopover((prev) => !prev)}>
                <ResizeCameraIcon />
              </ZoomButton>
            }
            side="top"
            sideOffset={4}
          >
            {tCm("canvas.cameraScaleTitle")}
          </Tooltip>
        )}
        <ZoomButton onClick={zoomIn}>
          <Icon icon="ic_zoom_in" width={20} fill="none" stroke="currentColor" />
        </ZoomButton>
        <ZoomButton onClick={zoomOut}>
          <Icon icon="ic_zoom_out" width={20} fill="none" stroke="currentColor" />
        </ZoomButton>
      </ZoomButtons>
      {showScalePopover && onScaleCommit && (
        <CameraScalePopover
          iconScale={iconSizeLocal}
          onIconScaleChange={setIconSizeLocal}
          onIconScaleCommit={(v) => onScaleCommit({ iconSize: v })}
          labelScale={labelSizeLocal}
          onLabelScaleChange={setLabelSizeLocal}
          onLabelScaleCommit={(v) => onScaleCommit({ labelSize: v })}
          onClose={() => setShowScalePopover(false)}
        />
      )}
      <DeleteConfirmDialog
        open={deleteConfirm !== null}
        title={tCm(
          deleteConfirm?.memoKind === "diagram"
            ? "dialog.deleteFloorPlanDiagramTitle"
            : "dialog.deleteFloorPlanTextTitle",
        )}
        name=""
        messageKey=""
        message={tCm(
          deleteConfirm?.memoKind === "diagram"
            ? "dialog.deleteFloorPlanDiagramMessage"
            : "dialog.deleteFloorPlanTextMessage",
        )}
        ns="camera-mapping"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Container>
  );
}

// --- Styles ---

const Container = styled.div<{ $extraBasis?: number }>`
  flex: 1 1 ${({ $extraBasis }) => ($extraBasis ? `${$extraBasis}px` : "0%")};
  min-height: 0;
  overflow: hidden;
  position: relative;
  background: #f5f6fa;
  border-top: 1px solid rgba(143, 149, 178, 0.24);
`;

// (F17) 도면 뷰 좌상단 '표시할 도면' 셀렉트 오버레이.
// $top: 도면 단독 모드에선 상단 TopOverlay 가 덮으므로 topPadding 만큼 내려 노출.
const ViewSelect = styled.div<{ $top: number }>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  left: 12px;
  z-index: 7;
  width: 180px;
`;

const ViewSelectTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  height: 32px;
  padding: 0 8px 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(143, 149, 178, 0.18);
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  cursor: pointer;
  box-sizing: border-box;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    border-color: ${token("colors.brand.primary")};
  }
`;

const ViewSelectMenu = styled.div`
  margin-top: 2px;
  max-height: 240px;
  overflow-y: auto;
  padding: 8px 0;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #E6E8F0;
  box-shadow:
    -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
  display: flex;
  flex-direction: column;
`;

const ViewSelectItem = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border: none;
  background: ${({ $selected }) => ($selected ? "rgba(114, 142, 255, 0.08)" : "transparent")};
  color: ${token("colors.text.primary")};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $selected }) => ($selected ? "rgba(114, 142, 255, 0.12)" : token("colors.action.hover"))};
  }
`;

const DrawingBanner = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 8px;
  background: rgba(36, 41, 70, 0.85);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: auto;

  strong {
    color: #70ff00;
  }
`;

const CancelButton = styled.button`
  padding: 2px 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ZoomButtons = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 60px;
  right: 12px;
  z-index: 5;
  gap: 4px;
  width: 32px;
`;

const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: #242946;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;

const LabelToggleWrap = styled.div`
  position: absolute;
  bottom: 132px;
  right: 12px;
  z-index: 5;
`;

const LabelToggle = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "rgba(72, 75, 210, 0.16)" : "transparent")};
  color: ${({ $active }) => ($active ? "#484BD2" : "#242946")};

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(72, 75, 210, 0.24)" : token("colors.action.hover")};
  }
`;

const MemoToolbar = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  gap: 4px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const MemoToolButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "rgba(72, 75, 210, 0.16)" : "transparent")};
  color: ${({ $active }) => ($active ? "#484BD2" : "#242946")};

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(72, 75, 210, 0.24)" : token("colors.action.hover")};
  }
`;
